import { Icon } from "@iconify/react/dist/iconify.js";
import {
  Avatar,
  Button,
  Group,
  Menu,
  Modal,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { Form, Link, useFetcher } from "@remix-run/react";
import { useMemo, useState } from "react";

import {
  GetUserAvatarQuery,
  GetUserPermissionQuery,
} from "~/graphql/__generated__/types";

export default function AvatarButton({
  user,
  isImpersonating,
  impersonationTargetName,
}: {
  user: GetUserPermissionQuery["me"] | GetUserAvatarQuery["me"];
  isImpersonating?: boolean;
  impersonationTargetName?: string | null;
}) {
  const [stopModalOpened, setStopModalOpened] = useState(false);
  const fetcher = useFetcher();
  const photo = useMemo(() => user.pegawai?.filePhoto?.pathUrl, [user.pegawai]);

  const handleStop = () => {
    setStopModalOpened(false);
    fetcher.submit(
      {},
      {
        method: "post",
        encType: "application/json",
        action: "/impersonate/stop",
      },
    );
  };

  return (
    <>
      <Menu shadow="md" width={200}>
        <Menu.Target>
          <UnstyledButton className="block bg-white rounded-full p-1">
            <Group>
              <Avatar
                radius="xl"
                size={36}
                src={photo}
                alt={user.username}
                color="tmGreen"
                variant="gradient"
              />

              <div className="hidden md:block flex-1 w-32">
                <Text size="sm" c={"dark"} fw={600}>
                  {user.username}
                </Text>

                <Text c="dimmed" size="xs" className="text-ellipsis">
                  {user.pegawai?.cabang?.nama ?? "-"}
                </Text>
              </div>
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>{user.username}</Menu.Label>
          {isImpersonating ? (
            <Menu.Item
              color="orange"
              leftSection={
                <Icon icon="tabler:user-shield" className="w-4 h-4" />
              }
              onClick={() => setStopModalOpened(true)}
            >
              Stop Impersonate ({impersonationTargetName ?? "user"})
            </Menu.Item>
          ) : null}
          <Menu.Item
            leftSection={<Icon icon="tabler:user" className="w-4 h-4" />}
          >
            <Link to={`/dashboard/profile/${user.pegawaiId}`}>Profile</Link>
          </Menu.Item>
          <Menu.Divider />
          <Form method="post" action="/auth/v2/logout">
            <Menu.Item
              type="submit"
              color="red"
              leftSection={<Icon icon="tabler:logout" className="w-4 h-4" />}
            >
              Logout
            </Menu.Item>
          </Form>
        </Menu.Dropdown>
      </Menu>

      <Modal
        opened={stopModalOpened}
        onClose={() => setStopModalOpened(false)}
        title="Hentikan Impersonate"
        centered
      >
        <Text size="sm" mb="lg">
          Yakin ingin menghentikan impersonate atas{" "}
          <strong>{impersonationTargetName ?? "user"}</strong> dan kembali ke
          mode superadmin?
        </Text>
        <Group justify="flex-end">
          <Button variant="default" onClick={() => setStopModalOpened(false)}>
            Batal
          </Button>
          <Button
            color="red"
            loading={fetcher.state !== "idle"}
            onClick={handleStop}
          >
            Hentikan Impersonate
          </Button>
        </Group>
      </Modal>
    </>
  );
}
